import React, { useState, useEffect, useRef } from "react";
import { X, Plus, Pin, Trash2, Tag, Bold, Italic, Underline, Strikethrough, List, ImagePlus, Sparkles, Check, Mic, MicOff } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = [
  { label: "Yellow", value: "#fbbf24", text: "#92400e" },
  { label: "Green",  value: "#86efac", text: "#14532d" },
  { label: "Blue",   value: "#93c5fd", text: "#1e3a5f" },
  { label: "Pink",   value: "#f9a8d4", text: "#831843" },
  { label: "Purple", value: "#c4b5fd", text: "#3b0764" },
  { label: "Orange", value: "#fdba74", text: "#7c2d12" },
];

const ALLOWED_TAGS = new Set(["B","STRONG","I","EM","U","S","STRIKE","DEL","UL","OL","LI","BR","DIV","P","SPAN"]);

// Notes are shared between users, so only a small set of formatting tags is kept; all attributes are dropped.
function sanitize(html) {
  const doc = new DOMParser().parseFromString(`<body>${html || ""}</body>`, "text/html");
  const walk = (node) => {
    [...node.childNodes].forEach(ch => {
      if (ch.nodeType === 3) return;
      if (ch.nodeType !== 1 || !ALLOWED_TAGS.has(ch.tagName)) {
        if (ch.nodeType === 1 && ["SCRIPT","STYLE"].includes(ch.tagName)) { ch.remove(); return; }
        walk(ch);
        while (ch.firstChild) node.insertBefore(ch.firstChild, ch);
        ch.remove();
        return;
      }
      [...ch.attributes].forEach(a => ch.removeAttribute(a.name));
      walk(ch);
    });
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

const escapeHtml = (t) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Older notes were saved as plain text; newer ones are sanitised HTML.
const toHtml = (content) => /<[a-z][\s\S]*>/i.test(content || "") ? sanitize(content) : escapeHtml(content || "").replace(/\n/g, "<br>");

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, 800 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.8));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const AI_ACTIONS = [
  { key: "improve", label: "Improve writing" },
  { key: "fix", label: "Fix spelling" },
  { key: "summarize", label: "Summarize" },
  { key: "bullets", label: "Make a list" },
];

// Formatting toolbar + editable area + pictures + AI, shared by the new-note form and in-place editing.
function RichEditor({ initialHtml, initialImages, onChange, headers, minHeight = 100, textColor = "#fff" }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [images, setImages] = useState(initialImages || []);
  const [aiOpen, setAiOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);

  useEffect(() => () => { try { if (recogRef.current) { recogRef.current._userStop = true; recogRef.current.stop(); } } catch (e) { /* ignore */ } }, []);
  useEffect(() => { if (ref.current) { ref.current.innerHTML = initialHtml || ""; ref.current.focus(); } }, []); // eslint-disable-line

  const emit = (imgs = images) => onChange({ html: sanitize(ref.current.innerHTML), text: ref.current.innerText, images: imgs });
  const cmd = (c) => { ref.current.focus(); document.execCommand(c); emit(); };

  const addPictures = async (files) => {
    setErr("");
    try {
      const room = 6 - images.length;
      const next = [...images];
      for (const f of [...files].slice(0, Math.max(room, 0))) {
        if (f.type.startsWith("image/")) next.push(await fileToDataUrl(f));
      }
      setImages(next); emit(next);
    } catch (e) { setErr("Could not read that picture."); }
  };

  // Voice: dictate into the note. The mic is NOT pre-acquired with getUserMedia (that breaks SpeechRecognition, see HeroLanding.jsx).
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setErr("Voice is not supported here. Please use Chrome, Edge or Safari."); return; }
    if (listening) {
      try { recogRef.current._userStop = true; recogRef.current.stop(); } catch (e) { /* ignore */ }
      setListening(false);
      return;
    }
    setErr("");
    const r = new SR();
    r.lang = "en-US"; r.interimResults = false; r.continuous = true; r.maxAlternatives = 1;
    let retried = false, heard = false;
    r.onstart = () => setListening(true);
    r.onresult = (ev) => {
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (!ev.results[i].isFinal) continue;
        const t = ev.results[i][0].transcript.trim();
        if (!t) continue;
        heard = true;
        ref.current.focus();
        document.execCommand("insertText", false, t + " ");
        emit();
      }
    };
    r.onerror = (ev) => {
      const code = ev?.error || "unknown";
      if (code === "no-speech" && !heard && !retried && !r._userStop) { retried = true; try { r.start(); return; } catch (e) { /* fall through */ } }
      const map = { "not-allowed": "Microphone permission was denied.", "service-not-allowed": "The browser blocked the microphone.", "no-speech": "Didn't catch that. Try speaking closer to the mic.", "audio-capture": "No microphone found.", "network": "Network error during voice.", "aborted": "" };
      const label = map[code];
      if (label !== "") setErr(label || `Voice error: ${code}`);
      setListening(false);
    };
    r.onend = () => setListening(false);
    recogRef.current = r;
    try { r.start(); } catch (e) { setErr("Could not start the microphone. Reload the page and try again."); }
  };

  const runAI = async (action) => {
    setAiOpen(false); setErr("");
    const text = ref.current.innerText.trim();
    if (!text) { setErr("Write something first, then use AI."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/sticky-notes/ai`, { action, text }, { headers });
      ref.current.innerHTML = escapeHtml(data.text || "").replace(/\n/g, "<br>");
      emit();
    } catch (e) {
      setErr(e?.response?.data?.detail || "AI is not available right now.");
    } finally { setBusy(false); }
  };

  const Btn = ({ title, onClick, children, testid }) => (
    <button type="button" title={title} data-testid={testid} onMouseDown={e => e.preventDefault()} onClick={onClick}
      className="p-1.5 rounded hover:bg-white/10 text-gray-300 transition-colors">{children}</button>
  );

  return (
    <div>
      <div className="flex items-center gap-0.5 mb-1.5 relative flex-wrap" data-testid="sticky-toolbar">
        <Btn title="Bold" testid="fmt-bold" onClick={() => cmd("bold")}><Bold size={13} /></Btn>
        <Btn title="Italic" testid="fmt-italic" onClick={() => cmd("italic")}><Italic size={13} /></Btn>
        <Btn title="Underline" testid="fmt-underline" onClick={() => cmd("underline")}><Underline size={13} /></Btn>
        <Btn title="Strikethrough" testid="fmt-strike" onClick={() => cmd("strikeThrough")}><Strikethrough size={13} /></Btn>
        <Btn title="Bullet list" testid="fmt-bullets" onClick={() => cmd("insertUnorderedList")}><List size={13} /></Btn>
        <Btn title="Attach picture" testid="fmt-attach" onClick={() => fileRef.current.click()}><ImagePlus size={13} /></Btn>
        <Btn title={listening ? "Stop voice" : "Write with voice"} testid="fmt-voice" onClick={toggleVoice}>
          {listening ? <MicOff size={13} className="text-red-400" /> : <Mic size={13} />}
        </Btn>
        <Btn title="AI" testid="fmt-ai" onClick={() => setAiOpen(o => !o)}><Sparkles size={13} className="text-purple-300" /></Btn>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={e => { addPictures(e.target.files); e.target.value = ""; }} />
        {aiOpen && (
          <div className="absolute top-full left-0 mt-1 z-10 bg-[#1e293b] border border-white/10 rounded-lg py-1 w-40 shadow-xl">
            {AI_ACTIONS.map(a => (
              <button key={a.key} type="button" data-testid={`ai-${a.key}`} onMouseDown={e => e.preventDefault()} onClick={() => runAI(a.key)}
                className="w-full text-left px-3 py-1.5 text-xs text-gray-200 hover:bg-white/10">{a.label}</button>
            ))}
          </div>
        )}
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning data-testid="sticky-editor" onInput={() => emit()}
        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm outline-none focus:border-blue-500 overflow-y-auto leading-relaxed"
        style={{ minHeight, maxHeight: 220, color: textColor }} />
      {listening && <p data-testid="sticky-listening" className="text-[11px] text-red-300 mt-1">Listening... speak now. Click the mic again to stop.</p>}
      {busy && <p className="text-[11px] text-purple-300 mt-1">AI is working...</p>}
      {err && <p data-testid="sticky-editor-error" className="text-[11px] text-red-400 mt-1">{err}</p>}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {images.map((src, i) => (
            <div key={i} className="relative">
              <img src={src} alt="" className="h-14 w-14 object-cover rounded-md border border-white/10" />
              <button type="button" title="Remove picture" onClick={() => { const n = images.filter((_, j) => j !== i); setImages(n); emit(n); }}
                className="absolute -top-1 -right-1 bg-black/70 rounded-full p-0.5 text-white"><X size={10} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NoteCard({ note, onDelete, onPin, onEdit, isOwner, headers }) {
  const c = COLORS.find(c => c.value === note.color) || COLORS[0];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saveErr, setSaveErr] = useState("");

  const save = async () => {
    if (!draft) { setEditing(false); return; }
    if (!draft.text.trim() && draft.images.length === 0) { setSaveErr("A note can't be empty."); return; }
    try {
      await onEdit(note.id, { content: draft.html, images: draft.images });
      setEditing(false); setSaveErr("");
    } catch (e) { setSaveErr(e?.response?.data?.detail || "Could not save. Please try again."); }
  };

  const taggees = (note.tagged_users || []).map(u => u.split("@")[0]);

  return (
    <div
      data-testid={`sticky-note-${note.id}`}
      className="relative rounded-xl shadow-md mb-3 transition-all hover:shadow-lg"
      style={{ background: c.value, color: c.text, minHeight: 90 }}
    >
      <div className="flex items-center justify-between px-3 pt-2 pb-1">
        <div className="flex gap-1 flex-wrap">
          {taggees.map(u => (
            <span key={u} className="text-xs px-1.5 py-0.5 rounded-full font-semibold opacity-70" style={{ background: "rgba(0,0,0,0.12)" }}>@{u}</span>
          ))}
        </div>
        {isOwner && (
          <div className="flex gap-1">
            {editing && (
              <button data-testid="save-note" onClick={save} title="Save" className="p-1 rounded hover:bg-black/10 transition-colors"><Check size={12} /></button>
            )}
            <button data-testid="pin-note" onClick={() => onPin(note.id, !note.pinned)} title={note.pinned ? "Unpin" : "Pin"}
              className="p-1 rounded hover:bg-black/10 transition-colors">
              <Pin size={12} fill={note.pinned ? c.text : "none"} />
            </button>
            <button data-testid="delete-note" onClick={() => onDelete(note.id)}
              className="p-1 rounded hover:bg-black/10 transition-colors">
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>
      <div className="px-3 pb-3">
        {editing ? (
          <div className="rounded-lg bg-[#0f172a] p-2">
            <RichEditor initialHtml={toHtml(note.content)} initialImages={note.images} headers={headers} minHeight={60} onChange={setDraft} />
            {saveErr && <p className="text-[11px] text-red-400 mt-1">{saveErr}</p>}
            <div className="flex gap-2 mt-2">
              <button onClick={save} className="text-xs px-3 py-1 rounded-md bg-blue-600 text-white">Save</button>
              <button onClick={() => { setEditing(false); setSaveErr(""); }} className="text-xs px-3 py-1 rounded-md bg-white/10 text-gray-300">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className="sticky-body text-sm font-medium cursor-text leading-relaxed"
              onClick={() => isOwner && setEditing(true)}
              dangerouslySetInnerHTML={{ __html: toHtml(note.content) || '<span style="opacity:.5;font-style:italic">Click to add text...</span>' }} />
            {(note.images || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {note.images.map((src, i) => (
                  <img key={i} src={src} alt="" className="max-h-32 max-w-full rounded-md border border-black/10" />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AddNoteModal({ onClose, onAdd, workspaceUsers, headers }) {
  const [draft, setDraft] = useState({ html: "", text: "", images: [] });
  const [color, setColor] = useState(COLORS[0].value);
  const [tagged, setTagged] = useState([]);
  const [userSearch, setUserSearch] = useState("");

  const filteredUsers = workspaceUsers.filter(u =>
    !tagged.includes(u.address) && (
      (u.name || "").toLowerCase().includes(userSearch.toLowerCase()) ||
      u.address.toLowerCase().includes(userSearch.toLowerCase())
    )
  );

  const [addErr, setAddErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.text.trim() && draft.images.length === 0) { setAddErr("Please type something into the note first."); return; }
    setSaving(true); setAddErr("");
    try {
      await onAdd({ content: draft.html, color, tagged_users: tagged, images: draft.images });
      onClose();
    } catch (err) {
      setAddErr(err?.response?.data?.detail || "Could not save the note. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 w-96 max-w-[95vw] shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-sm">New Sticky Note</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <RichEditor initialHtml="" initialImages={[]} headers={headers} onChange={setDraft} />
          {/* Color picker */}
          <div>
            <p className="text-xs text-gray-400 mb-2">Color</p>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map(c => (
                <button key={c.value} type="button" onClick={() => setColor(c.value)}
                  className="w-7 h-7 rounded-full border-2 transition-all"
                  style={{ background: c.value, borderColor: color === c.value ? "#fff" : "transparent" }} />
              ))}
            </div>
          </div>
          {/* Tag users */}
          <div>
            <p className="text-xs text-gray-400 mb-2 flex items-center gap-1"><Tag size={11} /> Tag Users</p>
            {tagged.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {tagged.map(addr => (
                  <span key={addr} className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                    @{addr.split("@")[0]}
                    <button type="button" onClick={() => setTagged(t => t.filter(x => x !== addr))}><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
            <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search users..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-blue-500" />
            {userSearch && filteredUsers.length > 0 && (
              <div className="bg-[#1e293b] border border-white/10 rounded-lg mt-1 max-h-28 overflow-y-auto">
                {filteredUsers.slice(0, 5).map(u => (
                  <button key={u.address} type="button" onClick={() => { setTagged(t => [...t, u.address]); setUserSearch(""); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-white/5 transition-colors">
                    {u.name || u.address.split("@")[0]} <span className="opacity-50">({u.address})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {addErr && (
            <div data-testid="sticky-add-error" className="text-[11px] text-red-400 mb-2">{addErr}</div>
          )}
          <button type="submit" disabled={saving} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50">
            {saving ? "Saving..." : "Add Note"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function StickyNotes({ visible, onToggle, currentUser }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const token = localStorage.getItem("aura_token");
  const panelRef = useRef(null);
  const [pos, setPos] = useState(() => {
    try { const p = JSON.parse(localStorage.getItem("sticky_panel_pos")); if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) return p; } catch (e) { /* ignore */ }
    return { x: Math.max(window.innerWidth - 292, 8), y: 64 };
  });
  const [size, setSize] = useState(() => {
    try { const z = JSON.parse(localStorage.getItem("sticky_panel_size")); if (z && z.w >= 220) return z; } catch (e) { /* ignore */ }
    return null;
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchNotes = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/sticky-notes`, { headers });
      setNotes(data || []);
    } catch (e) {
      console.error("Sticky notes fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/users`, { headers });
      setWorkspaceUsers(data || []);
    } catch (e) { /* non-admin users won't have access */ }
  };

  useEffect(() => {
    if (visible) { fetchNotes(); fetchUsers(); }
  }, [visible]);

  const addNote = async (note) => {
    const { data } = await axios.post(`${API}/api/sticky-notes`, note, { headers });
    setNotes(prev => [data, ...prev]);
  };

  const editNote = async (id, patch) => {
    await axios.put(`${API}/api/sticky-notes/${id}`, patch, { headers });
    if (patch.content !== undefined) patch = { ...patch, content: patch.content.trim() };
    setNotes(prev => prev.map(n => n.id === id ? { ...n, ...patch } : n));
  };

  const pinNote = async (id, pinned) => {
    await editNote(id, { pinned });
    setNotes(prev => {
      const updated = prev.map(n => n.id === id ? { ...n, pinned } : n);
      return [...updated.filter(n => n.pinned), ...updated.filter(n => !n.pinned)];
    });
  };

  const deleteNote = async (id) => {
    await axios.delete(`${API}/api/sticky-notes/${id}`, { headers });
    setNotes(prev => prev.filter(n => n.id !== id));
  };

  const startDrag = (e) => {
    if (e.target.closest("button")) return;
    const sx = e.clientX, sy = e.clientY, ox = pos.x, oy = pos.y;
    const move = (ev) => {
      const w = panelRef.current ? panelRef.current.offsetWidth : 240;
      setPos({ x: Math.min(Math.max(ox + ev.clientX - sx, 0), window.innerWidth - w), y: Math.min(Math.max(oy + ev.clientY - sy, 52), window.innerHeight - 60) });
    };
    const up = () => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up);
      setPos(p => { try { localStorage.setItem("sticky_panel_pos", JSON.stringify(p)); } catch (err) { /* ignore */ } return p; });
    };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const saveSize = () => {
    const el = panelRef.current;
    if (!el) return;
    const z = { w: el.offsetWidth, h: el.offsetHeight };
    setSize(z);
    try { localStorage.setItem("sticky_panel_size", JSON.stringify(z)); } catch (err) { /* ignore */ }
  };

  if (!visible) return null;

  const myAddr = currentUser?.address || "";

  return (
    <>
      {showAdd && (
        <AddNoteModal onClose={() => setShowAdd(false)} onAdd={addNote} workspaceUsers={workspaceUsers} headers={headers} />
      )}
      <style>{`.sticky-body ul,[data-testid="sticky-editor"] ul{list-style:disc;padding-left:1.2em}.sticky-body ol,[data-testid="sticky-editor"] ol{list-style:decimal;padding-left:1.2em}`}</style>
      <div
        data-testid="sticky-notes-panel"
        ref={panelRef}
        onMouseUp={saveSize}
        onTouchEnd={saveSize}
        className="fixed z-[50] flex flex-col rounded-xl overflow-hidden shadow-2xl border border-white/[0.08]"
        style={{ left: pos.x, top: pos.y, width: size ? size.w : 260, height: size ? size.h : undefined, minWidth: 220, minHeight: 120, maxWidth: "95vw", maxHeight: "calc(100vh - 60px)", resize: "both", pointerEvents: "auto" }}
      >
        <div className="bg-[#0b1120]/95 backdrop-blur-xl flex flex-col h-full max-h-full overflow-hidden">
          {/* Header: drag here to move the panel */}
          <div data-testid="sticky-drag-handle" onPointerDown={startDrag} style={{ cursor: "move", touchAction: "none" }}
            className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] select-none">
            <span className="text-white font-bold text-xs uppercase tracking-widest">Sticky Notes</span>
            <div className="flex gap-1">
              <button data-testid="add-sticky-note" onClick={() => setShowAdd(true)}
                className="p-1.5 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/40 transition-colors">
                <Plus size={13} />
              </button>
              <button onClick={onToggle} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 transition-colors">
                <X size={13} />
              </button>
            </div>
          </div>
          {/* Notes list */}
          <div className="flex-1 overflow-y-auto p-3">
            {loading ? (
              <p className="text-xs text-gray-500 text-center mt-8">Loading...</p>
            ) : notes.length === 0 ? (
              <div className="text-center mt-10">
                <p className="text-gray-500 text-xs">No notes yet.</p>
                <button onClick={() => setShowAdd(true)}
                  className="mt-3 text-xs text-blue-400 hover:text-blue-300 transition-colors">
                  + Add your first note
                </button>
              </div>
            ) : (
              notes.map(n => (
                <NoteCard key={n.id} note={n} isOwner={n.owner === myAddr}
                  onDelete={deleteNote} onPin={pinNote} onEdit={editNote} headers={headers} />
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
